'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Formik, FormikHelpers } from 'formik';

import { CreateDonationRequestModeEnum, Donation, Project, Subproject, User } from '@/app/api';
import { DonationFormBaseValues } from '@/app/components/DonationForm/types';
import { useSingleDonationFormValidationSchema } from '@/app/components/DonationForm/useDonationFormValidationSchema';
import { SelectOption } from '@/app/components/Select/Select';
import { SUBPROJECT_SEARCH_PARAM_KEY } from '@/app/constants';
import { Country, DonationFormType, Language, PaymentMethod } from '@/app/enums';
import useAnalytics from '@/app/hooks/useAnalytics';
import { useToast } from '@/app/hooks/useToast';
import { useAbGroup } from '@/app/providers/ConfigProvider';
import { isResponseError } from '@/app/utils/apiCommonUtils';
import { donationsApiForClient } from '@/app/utils/apiForClient';
import { getCountryConfig } from '@/app/utils/countryConfig';
import { formatPrice } from '@/app/utils/formatting';
import { getCalculatedTip } from '@/app/utils/getCalculatedTip';
import { getDonationAmountIncludingAmountBasedTip, getDonationAmountIncludingTip } from '@/app/utils/numbers';

import {
  transformCreateDonationViolationsToDonationFormErrors,
  transformSingleDonationFormValuesToCreateDonationRequest,
} from '@/app/components/DonationForm/utils';

import { CustomAmount } from '@/app/components/DonationForm/donationFormParts/CustomAmount';
import * as Sentry from '@sentry/nextjs';

import Badge from '@/app/components/Badge/Badge';
import Button, { ButtonHTMLTag, ButtonSize, ButtonStyle } from '@/app/components/Button/Button';
import ButtonRadioGroup, { ButtonRadioGroupOption } from '@/app/components/ButtonRadioGroup/ButtonRadioGroup';
import CertificatePreviewDialog from '@/app/components/CertificatePreviewDialog/CertificatePreviewDialog';
import FormCheckbox from '@/app/components/Checkbox/FormCheckbox';
import FormDatePicker from '@/app/components/DatePicker/FormDatePicker';
import Divider from '@/app/components/Divider/Divider';
import DonateAvailableFundsWidget from '@/app/components/DonationForm/donationFormParts/DonateAvailableFundsWidget';
import DonationFormHeader from '@/app/components/DonationForm/donationFormParts/DonationFormHeader';
import DonationFormSection from '@/app/components/DonationForm/donationFormParts/DonationFormSection';
import { SubmitButton } from '@/app/components/DonationForm/donationFormParts/SubmitButton';
import SubprojectParentBox from '@/app/components/DonationForm/donationFormParts/SubprojectParentBox';
import { TipFormSection } from '@/app/components/DonationForm/donationFormParts/TipFormSection';
import DonationFormDisclaimer from '@/app/components/DonationFormDisclaimer/DonationFormDisclaimer';
import EyeIcon from '@/app/components/EyeIcon/EyeIcon';
import FormCard from '@/app/components/FormCard/FormCard';
import FormContainer from '@/app/components/FormContainer/FormContainer';
import FormRadioGroup, { RadioGroupOption } from '@/app/components/FormRadioGroup/FormRadioGroup';
import GiftIcon from '@/app/components/GiftIcon/GiftIcon';
import FormInput from '@/app/components/Input/FormInput';

import { DonationMatchingWidget } from '@/app/components/DonationForm/donationFormParts/DonationMatchingWidget';
import { DonationReceiptInfoInputs } from '@/app/components/DonationForm/donationFormParts/DonationReceiptInfoInputs';
import FormSelect from '@/app/components/Select/FormSelect';
import {
  getTipOptionsForHigherAmountBasedOnGroup,
  getTipOptionsForLowerAmountBasedOnGroup,
} from '@/app/utils/abTesting/checkout-form';
import {
  getDefaultAmountBasedValue,
  getMaxAmountBasedTipOption,
} from '@/app/utils/countryConfig/amountBasedTipOptions';

export interface SingleDonationFormBaseValues extends DonationFormBaseValues {
  amountPredesigned: number;
}

export interface SingleDonationFormValuesAuthenticatedUser extends SingleDonationFormBaseValues {
  formType: DonationFormType.SINGLE_AUTHENTICATED_USER;
  organizationId?: string;
}

export interface SingleDonationFormValuesUnauthenticatedUser extends SingleDonationFormBaseValues {
  formType: DonationFormType.SINGLE_UNAUTHENTICATED_USER;
  email: string;
}

interface SingleDonationFormProps<
  FormValuesType extends DonationFormBaseValues = SingleDonationFormValuesUnauthenticatedUser,
> {
  project: Project;
  subproject?: Subproject;
  initialValues?: Partial<FormValuesType>;
  countrySelectOptions: SelectOption<Country>[];
  amountOptions: RadioGroupOption<number>[];
  unassignedGoodhabits?: Donation[] | null;
  currentUser?: User;
  getPaymentMethodOptions: (
    donationMode: CreateDonationRequestModeEnum | null,
  ) => ButtonRadioGroupOption<PaymentMethod>[];
  onSuccess?: (donationId: string) => void;
  onFailure?: () => void;
  onCloseButtonClick?: () => void;
  shouldDisplayPrivacyConsentCheckbox?: boolean;
}

const countryConfig = getCountryConfig();

function SingleDonationForm<
  FormValuesType extends DonationFormBaseValues = SingleDonationFormValuesUnauthenticatedUser,
>({
  initialValues: defaultInitialValues,
  currentUser,
  project,
  subproject,
  countrySelectOptions,
  unassignedGoodhabits,
  getPaymentMethodOptions,
  onCloseButtonClick,
  onSuccess,
  onFailure,
  shouldDisplayPrivacyConsentCheckbox,
  amountOptions,
}: SingleDonationFormProps<FormValuesType>) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const router = useRouter();
  const {
    donation: { pushDonationCompletedEvent },
  } = useAnalytics();
  const { showErrorToast, showGenericErrorToast } = useToast();

  const [isCertificatePreviewDialogOpen, setIsCertificatePreviewDialogOpen] = useState(false);
  const searchParams = useSearchParams();
  const abGroup = useAbGroup();

  const tipOptionsForHighDonation = useMemo(() => {
    return getTipOptionsForHigherAmountBasedOnGroup(abGroup).map((option) => option.value);
  }, [abGroup]);

  const tipOptionsForLowDonation = useMemo(() => {
    return getTipOptionsForLowerAmountBasedOnGroup(abGroup).map((option) => option.value);
  }, [abGroup]);

  const subProject = searchParams.get(SUBPROJECT_SEARCH_PARAM_KEY) ?? undefined;

  const isTipDisabled = useMemo(() => project.settings.hasTipsDisabled, [project.settings.hasTipsDisabled]);

  const isFirstNameRequired = !currentUser?.firstName;
  const isLastNameRequired = !currentUser?.lastName;
  const isPhoneNumberRequired = project.settings.hasPhoneNumberRequired;

  const validationSchema = useSingleDonationFormValidationSchema({
    isAuthenticated: !!currentUser,
    isFirstNameRequired,
    isLastNameRequired,
    isTipDisabled,
    isPhoneNumberRequired,
    isCountryRequired: countryConfig.collectionDonationSettings.isCountryRequired && !currentUser?.address?.country,
    isBirthDateRequired: countryConfig.collectionDonationSettings.isDateOfBirthRequired && !currentUser?.birthdate,
  });

  const collectionLink = useMemo(() => {
    const languageQuery = language === Language.EN ? `?lang=${language}` : '';

    if (subproject) {
      return `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${project.slug}/${subproject.slug}${languageQuery}`;
    }

    return `${process.env.NEXT_PUBLIC_REDIRECT_URL}/${project.slug}${languageQuery}`;
  }, [project, subProject, language]);

  const getInitialAmountPredesigned = useCallback(
    (amount?: number) => {
      if (!amount) {
        return 600;
      }

      const correspondingOption = amountOptions.find((option) => option.value === amount);

      if (!correspondingOption) {
        return 0;
      }

      return correspondingOption.value;
    },
    [amountOptions],
  );

  const initialValues = useMemo<
    SingleDonationFormValuesAuthenticatedUser | SingleDonationFormValuesUnauthenticatedUser
  >(() => {
    const nameOnReceipt =
      currentUser?.firstName && currentUser?.lastName
        ? `${currentUser?.firstName} ${currentUser?.lastName}`
        : currentUser?.fullName;

    const amount = getInitialAmountPredesigned(defaultInitialValues?.amount);

    const base = {
      donationMode: CreateDonationRequestModeEnum.Single,
      paymentMethod: countryConfig.charityCollections.defaultPaymentMethod,
      amountPredesigned: getInitialAmountPredesigned(defaultInitialValues?.amount),
      amount: getInitialAmountPredesigned(defaultInitialValues?.amount),
      firstName: currentUser?.firstName ?? '',
      lastName: currentUser?.lastName ?? '',
      certificateOfGoodness: false,
      donationReceipt: currentUser?.wantsDonationReceipt ?? false,
      noNewsletter: false,
      agreedToShareContact: false,
      certificateRecipient: '',
      certificateDonor: '',
      nameOnReceipt: nameOnReceipt ?? '',
      phoneNumber: currentUser?.phoneNumber ?? '',
      streetAndHouseNumber: currentUser?.address?.street ?? '',
      city: currentUser?.address?.city ?? '',
      zipCode: currentUser?.address?.zip ?? '',
      tin: currentUser?.companyLegalId ?? '',
      birthDate: currentUser?.birthdate?.toISOString() ?? undefined,
      tipAmount: !project.settings.hasTipsDisabled ? getDefaultAmountBasedValue(amount) : undefined,
      tipPercentage: project.settings.hasTipsDisabled
        ? 0
        : countryConfig.donationFormCommon.defaultTipForAmountBelowBreakpoint,
      country: (currentUser?.address?.country as Country) ?? countryConfig.country,
    };

    if (!currentUser) {
      return {
        ...base,
        email: '',
        ...(defaultInitialValues ?? {}),
        formType: DonationFormType.SINGLE_UNAUTHENTICATED_USER,
      };
    }

    return {
      ...base,
      ...(defaultInitialValues ?? {}),
      formType: DonationFormType.SINGLE_AUTHENTICATED_USER,
    };
  }, [defaultInitialValues, currentUser, getInitialAmountPredesigned]);

  const handleCertificatePreviewButtonClick = useCallback(() => {
    setIsCertificatePreviewDialogOpen(true);
  }, [setIsCertificatePreviewDialogOpen]);

  const handleCloseCertificatePreviewDialog = useCallback(() => {
    setIsCertificatePreviewDialogOpen(false);
  }, [setIsCertificatePreviewDialogOpen]);

  const handleSubmit = useCallback(
    async (
      values: SingleDonationFormValuesAuthenticatedUser | SingleDonationFormValuesUnauthenticatedUser,
      helpers: FormikHelpers<SingleDonationFormValuesAuthenticatedUser | SingleDonationFormValuesUnauthenticatedUser>,
    ) => {
      try {
        const transformedValues = transformSingleDonationFormValuesToCreateDonationRequest(
          project.id,
          values,
          currentUser?.id,
          subProject,
          abGroup,
        );

        const response = await donationsApiForClient.postCreateDonation({
          createDonationRequest: transformedValues,
        });

        onSuccess?.(response.id);

        if (values.paymentMethod !== PaymentMethod.Card) {
          pushDonationCompletedEvent({
            donationId: response.id,
            requestDto: transformedValues,
            project,
            paymentMethod: values.paymentMethod,
          });
        }

        const baseUrl = process.env.NEXT_PUBLIC_REDIRECT_URL;
        const queryString = values.paymentMethod === PaymentMethod.Card ? '?redirectToGateway=1' : '';

        if (baseUrl?.endsWith('/')) {
          return router.push(`${baseUrl}prispevek/detail/${response.id}${queryString}`);
        }

        router.push(`${baseUrl}/prispevek/detail/${response.publicId}${queryString}`);
      } catch (error: unknown) {
        const isValidationError = isResponseError(error) && error.response.status === 422;

        if (!isValidationError) {
          console.log(error);
          console.log(values);
          Sentry.captureException(error);
        }

        onFailure?.();

        if (!isResponseError(error)) {
          return showGenericErrorToast();
        }

        switch (error.response.status) {
          case 422:
            const responseJson = await error.response.json();
            const transformedErrors = transformCreateDonationViolationsToDonationFormErrors(responseJson.violations);
            helpers.setErrors(transformedErrors);

            return showErrorToast(t('messages.422'));

          default:
            showGenericErrorToast();
        }
      }
      helpers.setSubmitting(false);
    },
    [
      onSuccess,
      onFailure,
      router,
      showErrorToast,
      showGenericErrorToast,
      project.id,
      isTipDisabled,
      currentUser,
      t,
      subProject,
    ],
  );

  return (
    <div className="w-full">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, values, setFieldValue, touched }) => (
          <FormCard onCloseButtonClick={onCloseButtonClick}>
            <FormContainer>
              <DonationFormHeader
                captionAtTheTop={t('components.DonationForm.donationPercentageGiven', { percentage: 100 })}
                collectionTitle={subproject?.content.name ?? project.content.currentName ?? ''}
                collectionImage={project.content.mainPhoto?.sizes.miniCropped.path}
                collectionLink={collectionLink}
              />
              {!!subproject && <SubprojectParentBox parentTitle={project.content.currentName} className="mb-[40px]" />}
              {!!unassignedGoodhabits?.length && (
                <>
                  <DonateAvailableFundsWidget availableFunds={unassignedGoodhabits} className="mb-[32px]" />
                  <h2>{t('components.DonationForm.orSubmitNewDonation')}</h2>
                </>
              )}
              <DonationFormSection order={1} title={t('components.DonationForm.supportTheStory')}>
                <FormRadioGroup<number>
                  name="amountPredesigned"
                  label={t('components.DonationForm.amount')}
                  options={amountOptions}
                  columnsCount={2}
                  onChange={(value) => {
                    const previousAmount = Number(values.amount);

                    setFieldValue('amount', value || '');

                    if (!project.settings.hasTipsDisabled) {
                      const [fieldName, calculatedValue] = getCalculatedTip({
                        currentTip: values.tipPercentage,
                        abGroup,
                        amount: Number(value),
                        tipBreakpoint: countryConfig.donationFormCommon.tipBreakpointForDefaultValue,
                        tipOptionsForHighDonation,
                        tipOptionsForLowDonation,
                        previousAmount,
                        defaultTipForAmountAboveBreakpoint:
                          countryConfig.donationFormCommon.defaultTipForAmountAboveBreakpoint,
                        defaultTipForAmountBelowBreakpoint:
                          countryConfig.donationFormCommon.defaultTipForAmountBelowBreakpoint,
                      });

                      setFieldValue(fieldName, calculatedValue);
                    }
                  }}
                />
                {values.amountPredesigned === 0 && (
                  <div className="mt-[16px]">
                    <CustomAmount
                      currentTip={values.tipPercentage}
                      label={t('components.DonationForm.anotherAmount')}
                      isTipTouched={!!touched.tipPercentage}
                      onTipChange={([fieldName, value]) => setFieldValue(fieldName, value)}
                    />
                  </div>
                )}
              </DonationFormSection>
              <DonationFormSection order={2} title={t('components.DonationForm.paymentType')}>
                {project.donationMatching && !subProject && (
                  <DonationMatchingWidget
                    currentDonationValue={values.amount}
                    donationMatching={project.donationMatching}
                  />
                )}
                <ButtonRadioGroup
                  name="paymentMethod"
                  label={t('components.DonationForm.paymentType')}
                  options={getPaymentMethodOptions(CreateDonationRequestModeEnum.Single)}
                />
              </DonationFormSection>
              <Divider className="mb-[56px] mt-[64px]" />
              <DonationFormSection
                order={3}
                title={t('components.DonationForm.donorInfo')}
                description={
                  countryConfig.collectionDonationSettings.displayNewLawDisclaimer
                    ? t('components.DonationForm.newLawAboutPersonalInfoDisclaimerForDobro')
                    : t('components.DonationForm.thisInfoIsPrivate')
                }
              >
                {!currentUser && (
                  <FormInput
                    name="email"
                    label={t('components.DonationForm.email')}
                    note={t('components.DonationForm.emailNote')}
                  />
                )}

                {isFirstNameRequired && (
                  <FormInput
                    name="firstName"
                    label={t('components.DonationForm.firstName')}
                    onBlur={() => {
                      if (touched.nameOnReceipt) {
                        return;
                      }

                      const fullName = [values.firstName ?? undefined, values.lastName ?? undefined]
                        .filter(Boolean)
                        .join(' ');

                      setFieldValue('nameOnReceipt', fullName);
                    }}
                  />
                )}

                {isLastNameRequired && (
                  <FormInput
                    name="lastName"
                    label={t('components.DonationForm.lastName')}
                    onBlur={() => {
                      if (touched.nameOnReceipt) {
                        return;
                      }

                      const fullName = [values.firstName ?? undefined, values.lastName ?? undefined]
                        .filter(Boolean)
                        .join(' ');

                      setFieldValue('nameOnReceipt', fullName);
                    }}
                  />
                )}

                {countryConfig.collectionDonationSettings.isCountryRequired && !currentUser?.address.country && (
                  <FormSelect
                    name="country"
                    label={t('components.DonationForm.country')}
                    options={countrySelectOptions}
                  />
                )}
                {countryConfig.collectionDonationSettings.isDateOfBirthRequired &&
                  !currentUser?.birthdate &&
                  !values.donateAsLegalEntity && (
                    <FormDatePicker name="birthDate" label={t('components.DonationForm.birthDate')} />
                  )}

                {(!values.donationReceipt ||
                  !countryConfig.collectionDonationSettings.isAdditionalInfoRequiredForDonationReceipt) &&
                  isPhoneNumberRequired && (
                    <div className="mt-[16px]">
                      <FormInput
                        name="phoneNumber"
                        label={t('components.DonationForm.phoneNumberLabel')}
                        placeholder={countryConfig.donationFormCommon.phoneNumberFieldPlaceholder}
                      />
                    </div>
                  )}

                {countryConfig.collectionDonationSettings.displayDonationAsLegalEntityCheckbox && (
                  <>
                    <FormCheckbox
                      onChange={(value) => setFieldValue('donationReceipt', value)}
                      name="donateAsLegalEntity"
                      label={t('components.DonationForm.donateAsLegalEntity')}
                    />
                    {values.donateAsLegalEntity && (
                      <DonationReceiptInfoInputs
                        isCompanyNameRequired
                        displayPhoneNumber={false}
                        displayBirthDate={false}
                        displayCountrySelect={false}
                        countries={countrySelectOptions}
                      />
                    )}
                  </>
                )}

                {countryConfig.collectionDonationSettings.displayWhatYouGetLabel && (
                  <span className="block mb-[8px] font-medium text-[16px] leading-[24px]">
                    {t('components.DonationForm.whatYouGet')}
                  </span>
                )}
                {values.donateAsLegalEntity &&
                  countryConfig.collectionDonationSettings.shouldBeDonationReceiptCheckboxBeforeGoodnessCertificate && (
                    <FormCheckbox
                      name="donationReceipt"
                      label={t('components.DonationForm.donationReceiptCheckbox')}
                      note={t('components.DonationForm.donationReceiptNote')}
                    />
                  )}
                <FormCheckbox
                  testId="certificateOfGoodness"
                  name="certificateOfGoodness"
                  label={t('components.DonationForm.certificateOfGoodnessCheckbox')}
                  note={t('components.DonationForm.certificateOfGoodnessNote')}
                  accent={
                    <Badge
                      text={t('components.DonationForm.giftTip')}
                      className="relative"
                      icon={<GiftIcon className="w-[18px] h-[18px]" />}
                    />
                  }
                />

                {values.certificateOfGoodness && (
                  <div>
                    <FormInput
                      name="certificateRecipient"
                      label={t('components.DonationForm.certificateRecipient')}
                      note={t('components.DonationForm.certificateRecipientNote')}
                    />

                    <FormInput
                      name="certificateDonor"
                      label={t('components.DonationForm.certificateDonor')}
                      note={t('components.DonationForm.certificateDonorNote')}
                    />

                    <Button
                      testId="certificate-preview-button"
                      className="flex items-center mx-[auto] mb-[48px] !p-[18px] !tracking-[2px]"
                      size={ButtonSize.MEDIUM}
                      style={ButtonStyle.PRIMARY_OUTLINED}
                      tagSpecificProps={{
                        htmlTag: ButtonHTMLTag.BUTTON,
                        onClick: handleCertificatePreviewButtonClick,
                      }}
                    >
                      <EyeIcon className="mr-[10px]" />
                      {t('components.DonationForm.showPreview')}
                    </Button>
                  </div>
                )}

                {!countryConfig.collectionDonationSettings.shouldBeDonationReceiptCheckboxBeforeGoodnessCertificate && (
                  <FormCheckbox
                    name="donationReceipt"
                    label={t('components.DonationForm.donationReceiptCheckbox')}
                    note={t('components.DonationForm.donationReceiptNote')}
                  />
                )}

                {values.donationReceipt &&
                  countryConfig.collectionDonationSettings.isAdditionalInfoRequiredForDonationReceipt && (
                    <DonationReceiptInfoInputs
                      displayTinNote
                      displayPhoneNumber={isPhoneNumberRequired}
                      displayBirthDate={!currentUser?.birthdate}
                      displayCountrySelect={!currentUser?.address.country}
                      countries={countrySelectOptions}
                    />
                  )}

                {!currentUser?.subscribesToNewsletter && (
                  <FormCheckbox name="noNewsletter" label={t('components.DonationForm.noNewsletterCheckbox')} />
                )}
                {shouldDisplayPrivacyConsentCheckbox && (
                  <FormCheckbox
                    name="agreedToShareContact"
                    label={t('components.DonationForm.privacyConsentCheckbox')}
                  />
                )}

                <Divider className="my-[60px]" />
              </DonationFormSection>
              {!isTipDisabled && (
                <TipFormSection
                  amount={values.amount}
                  sectionTitle={t('components.DonationForm.supportDonio')}
                  onTipPercentageChange={() => setFieldValue('customTipAmount', '')}
                />
              )}
              <SubmitButton isSubmitting={isSubmitting}>
                {t('components.DonationForm.submit', {
                  amount: formatPrice(
                    values.amount <= getMaxAmountBasedTipOption()
                      ? getDonationAmountIncludingAmountBasedTip(
                          values.amount || 0,
                          values.tipAmount || 0,
                          values.customTipAmount,
                        )
                      : getDonationAmountIncludingTip(
                          values.amount || 0,
                          values.tipPercentage || 0,
                          values.customTipAmount,
                        ),
                  ),
                })}
              </SubmitButton>
              <DonationFormDisclaimer />
            </FormContainer>
          </FormCard>
        )}
      </Formik>
      <CertificatePreviewDialog isOpen={isCertificatePreviewDialogOpen} onClose={handleCloseCertificatePreviewDialog} />
    </div>
  );
}

export default SingleDonationForm;
